/**
 * O editor de ligações do pinout — as rotas que ele usa.
 *
 * - `GET  api/pinouts/grafo` — nós e arestas que o motor monta para desenhar, sem o
 *   desenho. Sai da MESMA árvore que o SVG, e isso foi medido em componentes
 *   reais (ApiSistema/ferramentas/ProvaDoGrafo): todo pino desenhado está no grafo.
 * - `POST api/pinouts/grafo/aplicar` — TODA escrita do editor: criar pino, apagar
 *   linha, ligar a um pino que já existe, marcar junção, criar junção, soltar
 *   etiqueta. Numa transação: aplica, confere com o motor e desfaz se a base
 *   piorar. `simular: true` faz o caminho inteiro e desfaz sempre.
 * - `GET  api/pinouts/grafo/nome`, `.../restricoes`, `.../componentes` — as
 *   leituras de enquanto se digita: o que um nome de fio faria, as restrições do
 *   modelo aberto e a busca de componente no servidor.
 *
 * Até 25/09/2026 criar e apagar iam direto em `POST`/`DELETE api/pinouts`, sem
 * conferência: apagar um dos dois pinos de uma junção deixava a outra órfã.
 *
 * **Recusa não é erro.** A rota de aplicar responde 200 quando recusa, com o
 * texto para o operador ler em `recusas`. Quem chama mostra o texto; não trata
 * como falha de rede.
 */

import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"
import { BUSCA_ESPERA_MS, BUSCA_MINIMO_CARACTERES } from "./query"
import type { ItemDeCatalogo } from "./catalogos"

export type CondicaoDeJuncao = "JuncaoOrfa" | "CuringaAcidental" | "RedeLarga"
export type FamiliaDeLigacao = "Juncao" | "Ligacao"

export type AchadoDeJuncao = {
  condicao: CondicaoDeJuncao
  familia: FamiliaDeLigacao
  projetoId: number
  componenteId: number | null
  pinoutId: number | null
  circuitoId: number
  circuito: string
  circuitoAlvoId: number | null
  circuitoAlvo: string | null
  alcance: number | null
  pinosLigados: number
  grave: boolean
  explicacao: string
}

/** As marcas que o servidor pendura no nó, da mais grave para a menos. */
export type MarcaDoNo = "redeLargaGrave" | "curinga" | "redeLarga" | "juncaoOrfa"

export type NoDoGrafo = {
  /** `p{pinout_id}`; o terra sem pino resolvido é `t{componente_id}`. */
  id: string
  pinoutId: number
  componenteId: number
  componenteCodigo: string
  componente: string
  componenteTipo: number
  conectorId: number
  conector: string
  pino: string
  circuitoId: number
  circuito: string
  cor: string
  /** Pino do componente de partida. */
  raiz: boolean
  /** Quantas vezes a travessia chegou aqui. Mais de uma = dois caminhos chegando no mesmo pino. */
  visitas: number
  achados: MarcaDoNo[]
  /** `{componente}|{prefixo}|{sufixo}` — mesmo valor, mesma junção. Vazio sem `#`. */
  grupoDeJuncao: string
}

export type TipoDeAresta = "ligacao" | "juncao" | "intermediario" | "aterramento"

export type ArestaDoGrafo = {
  de: string
  para: string
  tipo: TipoDeAresta
  /** Existe só porque um `_` virou curinga do LIKE: é um fio que não existe. */
  curinga: boolean
}

export type GrafoDoPinout = {
  projetoId: number
  modeloId: number
  componenteId: number
  nos: NoDoGrafo[]
  arestas: ArestaDoGrafo[]
  repetidos: string[]
  achados: AchadoDeJuncao[]
  segundos: number
  /** O componente de partida — vem mesmo sem nó nenhum, para a caixa vazia. */
  componenteCodigo?: string
  componente?: string
  componenteTipo?: number
}

export type PedidoDoGrafo = {
  projetoId: number
  componenteId: number
  modeloId: number
  simplificarAterramento: boolean
}

/**
 * Busca o grafo. **Não é rápido**: o motor faz uma consulta por pino visitado,
 * como no desenho — só não desenha. Por isso aceita cancelar e tem prazo largo.
 */
export async function buscarGrafo(p: PedidoDoGrafo, sinal?: AbortSignal): Promise<GrafoDoPinout> {
  const r = await api.get<GrafoDoPinout>("api/pinouts/grafo", {
    params: {
      projetoId: String(p.projetoId),
      componenteId: String(p.componenteId),
      modeloId: String(p.modeloId),
      simplificarAterramento: String(p.simplificarAterramento),
    },
    headers: { "Cache-Control": "no-cache" },
    signal: sinal,
    timeout: 300_000,
  })
  return r.data
}

/**
 * Um pino novo, como o "Lançar pinout" o descreve. Cada catálogo vai por id
 * (escolhido da lista) ou por texto — o servidor reconhece sem maiúscula e sem
 * espaço nas pontas, e cria o que não existir.
 */
export type NovoPino = {
  componenteId: number
  componenteCodigo: string
  /** Só vale para componente novo: 0 Normal, 1 Aterramento, 3 Junção... */
  componenteTipo: number
  conectorId: number
  conectorDescricao: string
  pino: string
  circuitoId: number
  circuitoDescricao: string
  corId: number
  corDescricao: string
  chicoteId: number
  chicoteDescricao: string
  /** 0 = todos os modelos. */
  restricaoId: number
  tipoPino: number
  obs: string
  arquivoOriginal: string
}

/** Um ramo da junção nova: vira `BASE.RAMO` na ponta distante e `BASE.RAMO#` na junção. */
export type RamoDaJuncao = {
  ramo: string
  pinoDaJuncao: string
  conectorId: number
  conectorDescricao: string
  corId: number
  corDescricao: string
  /** A ponta distante que já existe; se o fio dela tem outro nome, ele passa a BASE.RAMO. */
  pontaPinoutId: number
  /** Ou a ponta distante nova. */
  ponta: NovoPino | null
}

export type OperacaoDoEditor =
  | { tipo: "marcarJuncao"; componenteId: number; pinoutIds: number[] }
  | { tipo: "soltarEtiqueta"; pinoutId: number }
  | { tipo: "ligarExistente"; deId: number; paraId: number }
  | { tipo: "criarPino"; novoPino: NovoPino }
  | { tipo: "apagarPino"; pinoutId: number }
  | { tipo: "criarJuncao"; base: string; ramos: RamoDaJuncao[]; novoPino: NovoPino }

export type ContagemDeAchados = { juncaoOrfa: number; curinga: number; redeLarga: number; graves: number }

export type ResultadoDaEdicao = {
  aplicado: boolean
  simulado: boolean
  recusas: string[]
  alteracoes: string[]
  antes: ContagemDeAchados
  depois: ContagemDeAchados
  novosAchados: AchadoDeJuncao[]
  /** O que não impede gravar, mas o operador precisa saber (fio com uma ponta só). */
  avisos?: string[]
}

export async function aplicarEdicao(p: {
  projetoId: number
  modeloId: number
  simplificarAterramento: boolean
  operacoes: OperacaoDoEditor[]
  simular: boolean
}): Promise<ResultadoDaEdicao> {
  const r = await api.post<ResultadoDaEdicao>(
    "api/pinouts/grafo/aplicar", p, noProjeto(p.projetoId, { timeout: 300_000 }))
  return r.data
}

// ------------------------------------------------------------------ as leituras de enquanto se digita

export type CircuitoDoNome = {
  circuitoId: number
  descricao: string
  pinos: number
  /** Só casa porque um `_` virou curinga — é um fio que não existe. */
  curinga: boolean
  /** Até quatro pontas, "componente conector·pino". */
  onde: string[]
  corHabitual: string
}

/** O que um nome de fio faria no projeto, antes de gravar. */
export type ConferenciaDoNome = {
  nome: string
  /** Por que a conferência do lote vai recusar, se vai. */
  recusa: string | null
  /** Os circuitos que JÁ têm esse nome (sem maiúscula e sem espaço). Mais de um: o operador escolhe. */
  grafias: CircuitoDoNome[]
  /** O que o nome puxa pelo começo — as linhas a mais que o desenho vai mostrar. */
  alcanca: CircuitoDoNome[]
  /** Quem puxa o nome pelo começo dele. */
  alcancadoPor: CircuitoDoNome[]
}

export type RestricaoDoModelo = { id: number; descricao: string }
export type ComponenteDoEditor = { id: number; codigo: string; descricao: string; tipo: number; pinos: number }

/** `ignorar`: ao RENOMEAR, o próprio circuito não conta como "já existe" nem no alcance. */
export function conferirNome(projetoId: number, nome: string, sinal?: AbortSignal, ignorar = 0): Promise<ConferenciaDoNome> {
  const params = ignorar > 0 ? { projetoId, nome, ignorar } : { projetoId, nome }
  return api.get<ConferenciaDoNome>("api/pinouts/grafo/nome", { params, signal: sinal }).then((r) => r.data)
}

export function restricoesDoModelo(projetoId: number, modeloId: number): Promise<RestricaoDoModelo[]> {
  return obter<RestricaoDoModelo[]>("api/pinouts/grafo/restricoes", { projetoId, modeloId })
}

export function buscarComponentes(projetoId: number, termo: string): Promise<ComponenteDoEditor[]> {
  return obter<ComponenteDoEditor[]>("api/pinouts/grafo/componentes", { projetoId, termo })
}

/** As restrições ligadas ao modelo aberto — o editor só oferece estas, mais "todos os modelos". */
export function usarRestricoesDoModelo(projetoId: number, modeloId: number) {
  const { data, isPending } = useQuery({
    queryKey: ["editor-ligacoes", "restricoes", projetoId, modeloId],
    enabled: projetoId > 0 && modeloId > 0,
    staleTime: Infinity,
    queryFn: () => restricoesDoModelo(projetoId, modeloId),
  })
  return { itens: data ?? [], carregando: isPending }
}

/**
 * O "também liga em…" enquanto se digita (Q13). Espera o operador parar de
 * digitar, como o campo de busca, e não pergunta com o nome vazio.
 */
export function usarConferenciaDoNome(projetoId: number, nome: string, ignorar = 0) {
  const [termo, setTermo] = useState(nome.trim())
  useEffect(() => {
    const t = setTimeout(() => setTermo(nome.trim()), BUSCA_ESPERA_MS)
    return () => clearTimeout(t)
  }, [nome])
  const { data, isFetching } = useQuery({
    queryKey: ["editor-ligacoes", "nome", projetoId, termo, ignorar],
    enabled: projetoId > 0 && termo.length > 0,
    staleTime: 30_000,
    queryFn: ({ signal }) => conferirNome(projetoId, termo, signal, ignorar),
  })
  const emDia = termo === nome.trim() && data?.nome === termo
  return { conferencia: emDia ? data ?? null : null, carregando: isFetching || termo !== nome.trim() }
}

/** Componente buscado no servidor: a lista de catálogo é só a primeira página de 200. */
export function usarBuscaDeComponentes(projetoId: number) {
  const [termo, setTermo] = useState("")
  const { data, isFetching } = useQuery({
    queryKey: ["editor-ligacoes", "componentes", projetoId, termo],
    enabled: projetoId > 0 && termo.length >= BUSCA_MINIMO_CARACTERES,
    staleTime: 5 * 60_000,
    queryFn: () => buscarComponentes(projetoId, termo),
  })
  const itens: ItemDeCatalogo[] = (data ?? []).map((c) => ({
    id: c.id, codigo: c.codigo, rotulo: c.descricao && c.descricao !== c.codigo ? c.descricao : c.codigo, usos: c.pinos,
  }))
  const tipos = new Map((data ?? []).map((c) => [c.id, c.tipo]))
  return { itens, carregando: isFetching, buscar: setTermo, tipoDe: (id: number) => tipos.get(id) }
}
